import { classNames } from '@fl/cmsch-fe-library';
import React, { PropsWithChildren, ReactNode, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { AntLayout } from 'common/ant';
import { Config } from './config';
import styles from './styles.sass';

/** Props for the shared content shell that provides layout chrome and the page title. */
interface OuterProps {
  /** Enables the max-width content container used by narrower feature screens. */
  maxWidthContainer?: boolean;
  /** Adds standard page padding inside the content area. */
  havePadding?: boolean;
  /** Hides the global footer when a page needs a frameless surface. */
  noFooter?: boolean;
  /** Controls whether the content area stretches in a column or row layout. */
  flex?: 'column' | 'row';
  /** The routed page content rendered inside the shell. */
  children: ReactNode;
  /** Document title written to the browser tab. */
  browserTabTitle: string;
}

/**
 * Shared content shell for routed screens and overview pages.
 *
 * @param props Shell configuration and routed content.
 * @returns Page content wrapped in the shared layout chrome.
 *
 * @example
 * <Content browserTabTitle="Dashboard" havePadding>
 *     <Dashboard />
 * </Content>
 */
export const Content: React.FC<PropsWithChildren<OuterProps>> = ({
  maxWidthContainer,
  havePadding,
  noFooter,
  flex,
  children,
  browserTabTitle
}) => {
  const contentClassNames = useMemo(() => classNames(havePadding && styles.content, maxWidthContainer && styles.contentMaxWidthContainer, 'position-relative', flex && ['d-flex', `flex-${flex}`]), [havePadding, maxWidthContainer, flex]);
  return <>
            <Helmet title={browserTabTitle} data-sentry-element="Helmet" data-sentry-source-file="content.tsx" />
            <AntLayout data-sentry-element="AntLayout" data-sentry-source-file="content.tsx">
                <AntLayout.Content className={contentClassNames} data-sentry-element="unknown" data-sentry-source-file="content.tsx">
                    {children}
                </AntLayout.Content>
                {!noFooter && <AntLayout.Footer className="text-center">
                    {`Copyright © ${Config.year} Českomoravská společnost chovatelů, a.s.`}
                </AntLayout.Footer>}
            </AntLayout>
        </>;
};